// Records and reads "this media item is currently used on these pages."
// The public site calls recordUsage() when it picks a photo for a slot;
// the admin library reads getUsageForPhoto() to show the admin where each item appears.
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PageUsage {
  route: string;
  page_label: string;
  slot: string;
}

const recordedThisSession = new Set<string>();

/** Record a (photo_id, route, slot) usage. Idempotent thanks to UNIQUE constraint. */
export const recordUsage = async (
  photoId: string,
  route: string,
  pageLabel: string,
  slot: string,
) => {
  const key = `${photoId}|${route}|${slot}`;
  if (recordedThisSession.has(key)) return;
  recordedThisSession.add(key);

  // RLS allows anon + authenticated to insert usage rows for approved photos,
  // so we no longer gate this on an active session.
  await supabase
    .from("media_page_usage")
    .upsert(
      { photo_id: photoId, route, page_label: pageLabel, slot },
      { onConflict: "photo_id,route,slot", ignoreDuplicates: true },
    );
};

/** Hook: record usage for a list of photos appearing in the same slot on the current page. */
export const useRecordUsage = (
  photoIds: (string | undefined)[],
  route: string,
  pageLabel: string,
  slot: string,
) => {
  useEffect(() => {
    const ids = photoIds.filter((x): x is string => !!x);
    if (ids.length === 0) return;
    ids.forEach((id) => {
      recordUsage(id, route, pageLabel, slot);
    });
  }, [photoIds.join(","), route, pageLabel, slot]);
};

/** Look up where a single photo is currently used. */
export const usePhotoUsage = (photoId: string | undefined) =>
  useQuery({
    queryKey: ["media-usage", photoId],
    queryFn: async (): Promise<PageUsage[]> => {
      if (!photoId) return [];
      const { data } = await supabase
        .from("media_page_usage")
        .select("route, page_label, slot")
        .eq("photo_id", photoId)
        .order("page_label");
      return (data ?? []) as PageUsage[];
    },
    enabled: !!photoId,
    staleTime: 30_000,
  });

/** Bulk lookup for the library page: returns Map<photoId, PageUsage[]>. */
export const useUsageForPhotos = (photoIds: string[]) =>
  useQuery({
    queryKey: ["media-usage-bulk", photoIds.slice().sort().join(",")],
    queryFn: async () => {
      if (photoIds.length === 0) return new Map<string, PageUsage[]>();
      const { data } = await supabase
        .from("media_page_usage")
        .select("photo_id, route, page_label, slot")
        .in("photo_id", photoIds);
      const map = new Map<string, PageUsage[]>();
      (data ?? []).forEach((row: any) => {
        const list = map.get(row.photo_id) ?? [];
        list.push({ route: row.route, page_label: row.page_label, slot: row.slot });
        map.set(row.photo_id, list);
      });
      return map;
    },
    enabled: photoIds.length > 0,
    staleTime: 30_000,
  });
